import React, { useState, useEffect } from "react";
import {
  getOrders,
  updateOrder,
  ORDER_STATUSES,
} from "../../firebase/firestoreService";

const STATUS_CLASS = {
  Pending:   "pending",
  Confirmed: "confirmed",
  Shipped:   "shipped",
  Delivered: "delivered",
  Cancelled: "cancelled",
};

const fmt = (n) => `MK ${Number(n).toLocaleString()}`;

export default function OrdersSection() {
  const [orders,           setOrders]           = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [expandedId,       setExpandedId]       = useState(null);
  const [notes,            setNotes]            = useState({});
  const [savingNote,       setSavingNote]       = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  const [uploadProgress,   setUploadProgress]   = useState({});
  const [localReceipts,    setLocalReceipts]    = useState({});

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const data = await getOrders();
    setOrders(data);
    const initialNotes = {};
    data.forEach((o) => { initialNotes[o.id] = o.notes || ""; });
    setNotes(initialNotes);
    setLoading(false);
  };

  const handleStatusChange = async (id, newStatus) => {
    await updateOrder(id, { status: newStatus });
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o))
    );
  };

  const handleSaveNote = async (id) => {
    setSavingNote(id);
    await updateOrder(id, { notes: notes[id] || "" });
    setSavingNote(null);
  };

  const compressImage = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const scale  = Math.min(1, 1200 / img.width);
          canvas.width  = img.width  * scale;
          canvas.height = img.height * scale;
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.75));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

  const handleReceiptUpload = async (orderId, file) => {
    if (!file) return;
    setUploadingReceipt(orderId);
    setUploadProgress((prev) => ({ ...prev, [orderId]: 20 }));
    try {
      const dataUrl = await compressImage(file);
      setUploadProgress((prev) => ({ ...prev, [orderId]: 70 }));
      await updateOrder(orderId, { receiptUrl: dataUrl });
      setUploadProgress((prev) => ({ ...prev, [orderId]: 100 }));
      setLocalReceipts((prev) => ({ ...prev, [orderId]: dataUrl }));
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, receiptUrl: dataUrl } : o))
      );
    } catch (err) {
      console.error("Receipt upload error:", err);
    } finally {
      setUploadingReceipt(null);
    }
  };

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const buildWhatsAppUrl = (order) => {
    const phone = (order.customerPhone || "").replace(/\D/g, "");
    const ref   = (order.chargeId || order.id).slice(0, 8).toUpperCase();
    const hasReceipt = !!(localReceipts[order.id] || order.receiptUrl);
    const msg = hasReceipt
      ? `Hi ${order.customerName || "there"}, your Good Vibes Store order #${ref} has been dispatched! 📦 Your courier receipt is now available — open your profile on our site to view and download it. Total paid: MK ${Number(order.total || 0).toLocaleString()}. Thank you! 🛍️`
      : `Hi ${order.customerName || "there"}, just a quick update on your Good Vibes Store order #${ref}. Total: MK ${Number(order.total || 0).toLocaleString()}. Feel free to reach out if you have any questions! 🛍️`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  const DAY_FILTERS = [
    { key: "today",     label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "week",      label: "This Week" },
    { key: "month",     label: "This Month" },
    { key: "all",       label: "All Time" },
  ];

  const [dayFilter, setDayFilter] = useState("today");

  const inRange = (order) => {
    const ms = order.createdAt?.toMillis?.() || new Date(order.createdAt).getTime() || 0;
    const d  = new Date(ms);
    const now = new Date();
    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = startOfDay(now);
    if (dayFilter === "today")     return d >= today;
    if (dayFilter === "yesterday") {
      const yest = new Date(today); yest.setDate(yest.getDate() - 1);
      return d >= yest && d < today;
    }
    if (dayFilter === "week") {
      const weekStart = new Date(today); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return d >= weekStart;
    }
    if (dayFilter === "month")  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    return true;
  };

  const filtered = orders.filter(inRange);

  const counts = ORDER_STATUSES.reduce((acc, s) => {
    acc[s] = filtered.filter((o) => o.status === s).length;
    return acc;
  }, {});

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Orders</h2>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
          {DAY_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setDayFilter(f.key)}
              style={{
                padding: "5px 12px",
                fontSize: 11,
                fontFamily: "inherit",
                fontWeight: 600,
                letterSpacing: 0.3,
                borderRadius: 20,
                border: "1px solid",
                cursor: "pointer",
                transition: "all .15s",
                borderColor:  dayFilter === f.key ? "#1a1a1a" : "#ddd",
                background:   dayFilter === f.key ? "#1a1a1a" : "#fff",
                color:        dayFilter === f.key ? "#fff"    : "#888",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-card__label">
            {DAY_FILTERS.find(f => f.key === dayFilter)?.label} Orders
          </div>
          <div className="stat-card__value">{filtered.length}</div>
        </div>
        {ORDER_STATUSES.map((s) => (
          <div className="stat-card" key={s}>
            <div className="stat-card__label">{s}</div>
            <div className="stat-card__value">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="dash-empty">Loading orders…</p>
      ) : filtered.length === 0 ? (
        <p className="dash-empty">No orders for this period.</p>
      ) : (
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <React.Fragment key={order.id}>
                  <tr
                    onClick={() => toggleExpand(order.id)}
                    style={{ cursor: "pointer" }}
                    className={expandedId === order.id ? "orders__row--expanded" : ""}
                  >
                    <td style={{ fontFamily: "monospace", fontSize: 11, color: "#999" }}>
                      #{order.id.slice(0, 8).toUpperCase()}
                    </td>
                    <td>
                      <strong>{order.customerName || "—"}</strong>
                      <div style={{ fontSize: 11, color: "#999" }}>{order.customerPhone || ""}</div>
                    </td>
                    <td>
                      {(order.items || []).map((item, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#555" }}>
                          {item.name}{item.selColor ? ` · ${item.selColor}` : ""}{item.selSize ? ` · ${item.selSize}` : ""}{(item.qty || 1) > 1 ? ` × ${item.qty}` : ""}
                        </div>
                      ))}
                    </td>
                    <td><strong>{fmt(order.total || 0)}</strong></td>
                    <td style={{ fontSize: 12, color: "#999" }}>
                      {order.createdAt?.toDate
                        ? order.createdAt.toDate().toLocaleDateString("en-GB")
                        : "—"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        value={order.status || "Pending"}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        style={{
                          border: "1px solid #ddd", borderRadius: 6,
                          padding: "5px 8px", fontSize: 11,
                          fontFamily: "inherit", cursor: "pointer",
                          background: "#faf9f7",
                        }}
                      >
                        {ORDER_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <span
                        className={`status-badge status-badge--${STATUS_CLASS[order.status] || "pending"}`}
                        style={{ marginLeft: 8 }}
                      >
                        {order.status || "Pending"}
                      </span>
                      {order.deliveredByName && (
                        <div style={{ fontSize: 10, color: "#2e7d32", marginTop: 3 }}>
                          by {order.deliveredByName}
                        </div>
                      )}
                    </td>
                  </tr>

                  {expandedId === order.id && (
                    <tr className="orders__detail-row">
                      <td colSpan={6}>
                        <div className="orders__detail">

                          {/* Left: order info */}
                          <div className="orders__detail-left">
                            <p className="orders__detail-label">Delivery</p>
                            <p className="orders__detail-value">{order.deliverySummary || "—"}</p>
                            {order.deliveredByName && (
                              <>
                                <p className="orders__detail-label" style={{ marginTop: 10 }}>Delivered by</p>
                                <p className="orders__detail-value" style={{ color: "#2e7d32", fontWeight: 600 }}>
                                  {order.deliveredByName}
                                </p>
                              </>
                            )}
                            <p className="orders__detail-label" style={{ marginTop: 10 }}>Payment</p>
                            <p className="orders__detail-value">{order.paymentOperator || order.paymentMethod || "—"}</p>
                            <p className="orders__detail-label" style={{ marginTop: 10 }}>Phone</p>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <p className="orders__detail-value" style={{ margin: 0 }}>{order.customerPhone || "—"}</p>
                              {order.customerPhone && (
                                <a
                                  href={buildWhatsAppUrl(order)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    background: "#25D366", color: "#fff",
                                    fontSize: 11, fontWeight: 600, padding: "4px 10px",
                                    borderRadius: 6, textDecoration: "none", whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                  </svg>
                                  {(localReceipts[order.id] || order.receiptUrl) ? "Send Receipt" : "WhatsApp"}
                                </a>
                              )}
                            </div>

                            <div style={{ marginTop: 14 }}>
                              <label className="orders__detail-label">Tracking / Notes</label>
                              <textarea
                                className="orders__notes-input"
                                placeholder="e.g. Courier ref: GV-20240512…"
                                value={notes[order.id] || ""}
                                onChange={(e) => setNotes((prev) => ({ ...prev, [order.id]: e.target.value }))}
                                rows={2}
                              />
                              <button
                                className="btn-edit"
                                onClick={() => handleSaveNote(order.id)}
                                disabled={savingNote === order.id}
                              >
                                {savingNote === order.id ? "Saving…" : "Save Note"}
                              </button>
                            </div>
                          </div>

                          {/* Right: receipt upload */}
                          <div className="orders__detail-right">
                            <p className="orders__detail-label">Courier Receipt</p>

                            {(localReceipts[order.id] || order.receiptUrl) ? (
                              <div className="orders__receipt-preview">
                                <img
                                  src={localReceipts[order.id] || order.receiptUrl}
                                  alt="Courier receipt"
                                  className="orders__receipt-img"
                                />
                                <div className="orders__receipt-actions">
                                  <a
                                    href={localReceipts[order.id] || order.receiptUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-edit"
                                  >
                                    View
                                  </a>
                                  {uploadingReceipt === order.id ? (
                                    <span style={{ fontSize: 12, color: "#7C5C4A", marginLeft: 8 }}>
                                      {uploadProgress[order.id] || 0}%
                                    </span>
                                  ) : (
                                    <label className="btn-edit" style={{ cursor: "pointer", marginLeft: 6 }}>
                                      Replace
                                      <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => handleReceiptUpload(order.id, e.target.files[0])}
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>
                            ) : uploadingReceipt === order.id ? (
                              <div className="orders__receipt-progress-wrap">
                                <div className="orders__receipt-progress-bar">
                                  <div
                                    className="orders__receipt-progress-fill"
                                    style={{ width: `${uploadProgress[order.id] || 0}%` }}
                                  />
                                </div>
                                <span className="orders__receipt-progress-pct">
                                  {uploadProgress[order.id] || 0}%
                                </span>
                              </div>
                            ) : (
                              <label className="orders__receipt-drop">
                                <span className="orders__receipt-icon">📎</span>
                                <span>Click to upload receipt</span>
                                <span className="orders__receipt-hint">JPG or PNG</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={(e) => handleReceiptUpload(order.id, e.target.files[0])}
                                />
                              </label>
                            )}

                            <p className="orders__receipt-note">
                              Once uploaded, the customer can view and download this receipt from their profile.
                            </p>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
